/**
 * MODULE: platform/browser/frontend/components/BrowserSessionRestoredBanner.tsx
 * PURPOSE: Transient notice when an active session is reattached after refresh.
 */

import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const AUTO_DISMISS_MS = 6000;

interface BrowserSessionRestoredBannerProps {
  visible: boolean;
}

export function BrowserSessionRestoredBanner({ visible }: BrowserSessionRestoredBannerProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!visible) {
      setShow(false);
      return;
    }
    setShow(true);
    const timer = window.setTimeout(() => setShow(false), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!show) return null;

  return (
    <div
      className="flex items-center justify-between gap-3 border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-100"
      role="status"
      data-testid="browser-session-restored-banner"
    >
      <span>Session restored — your remote browser and recent agent activity are still active.</span>
      <button
        type="button"
        onClick={() => setShow(false)}
        className="shrink-0 rounded p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
        aria-label="Dismiss session restored notice"
      >
        <XMarkIcon className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
